import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { z } from 'zod';
import { useSignup } from '@/lib/auth';
import {
  EmailInUseError,
  MultiUserDisabledError,
  RateLimitedError,
  SignupRequestSchema,
  type SignupRequest,
} from '@/lib/api';

/**
 * Formulario de signup multi-user.
 *
 * En contraste con `SetupWizard` (que es single-user con passphrase y 3 pasos),
 * este form es de página única porque:
 * - el signup ocurre con frecuencia (un coach puede dar de alta varios
 *   atletas), y los pasos añaden fricción innecesaria;
 * - los campos de métricas (FTP/LTHR/T-pace) NO se piden aquí — el usuario
 *   los completa luego en /perfil. Mantenemos el signup mínimo.
 *
 * El backend hace auto-login al éxito (devuelve cookie de sesión + `ok:true`).
 * El router gating del root detecta `authenticated === true` tras invalidar la
 * query y redirige a `/`.
 */

type FormState = {
  email: string;
  password: string;
  passwordConfirm: string;
  name: string;
  age: string;
  sex: '' | 'M' | 'F' | 'X';
  weightKg: string;
  heightCm: string;
  timezone: string;
  units: 'metric' | 'imperial';
};

const INITIAL_STATE: FormState = {
  email: '',
  password: '',
  passwordConfirm: '',
  name: '',
  age: '',
  sex: '',
  weightKg: '',
  heightCm: '',
  timezone: 'Europe/Madrid',
  units: 'metric',
};

function optionalNumber(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : Number.NaN;
}

function optionalInt(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : Number.NaN;
}

function passwordStrength(p: string): { label: string; score: 0 | 1 | 2 | 3 | 4; color: string } {
  if (p.length < 8) return { label: 'Demasiado corta', score: 0, color: 'bg-rose-500' };
  let variety = 0;
  if (/[a-z]/.test(p)) variety++;
  if (/[A-Z]/.test(p)) variety++;
  if (/[0-9]/.test(p)) variety++;
  if (/[^a-zA-Z0-9]/.test(p)) variety++;
  const lengthBonus = p.length >= 16 ? 1 : 0;
  const score = Math.min(4, variety + lengthBonus) as 0 | 1 | 2 | 3 | 4;
  const labels = ['Débil', 'Débil', 'Aceptable', 'Buena', 'Fuerte'];
  const colors = ['bg-rose-500', 'bg-rose-500', 'bg-amber-500', 'bg-teal-500', 'bg-emerald-600'];
  return { label: labels[score], score, color: colors[score] };
}

function buildSignupBody(state: FormState): SignupRequest {
  return {
    email: state.email.trim(),
    password: state.password,
    name: state.name.trim(),
    age: optionalInt(state.age) ?? null,
    sex: state.sex === '' ? null : state.sex,
    weightKg: optionalNumber(state.weightKg) ?? null,
    heightCm: optionalNumber(state.heightCm) ?? null,
    timezone: state.timezone.trim() || 'Europe/Madrid',
    units: state.units,
  } as SignupRequest;
}

export function SignupForm(): ReactElement {
  const [state, setState] = useState<FormState>(INITIAL_STATE);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const signupMutation = useSignup();

  const strength = useMemo(() => passwordStrength(state.password), [state.password]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!state.email.trim()) errors.email = 'El email es obligatorio';
    if (state.password.length < 8) errors.password = 'Mínimo 8 caracteres';
    if (state.password !== state.passwordConfirm) {
      errors.passwordConfirm = 'Las contraseñas no coinciden';
    }
    if (!state.name.trim()) errors.name = 'El nombre es obligatorio';
    if (state.age && !/^\d+$/.test(state.age.trim())) errors.age = 'Debe ser un número entero';
    if (!state.timezone.trim()) errors.timezone = 'Timezone obligatoria';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (signupMutation.isPending) return;
    if (!validate()) return;
    const body = buildSignupBody(state);
    const result = SignupRequestSchema.safeParse(body);
    if (!result.success) {
      const errors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of (result.error as z.ZodError).issues) {
        const key = issue.path[0] as keyof FormState | undefined;
        if (key) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }
    signupMutation.mutate(result.data);
  }

  function topErrorMessage(): string | null {
    const err = signupMutation.error;
    if (!err) return null;
    if (err instanceof EmailInUseError) {
      return 'Ese email ya está registrado. ¿Quieres iniciar sesión?';
    }
    if (err instanceof MultiUserDisabledError) {
      return 'Esta instalación no permite registro de usuarios nuevos.';
    }
    if (err instanceof RateLimitedError) {
      const seconds = err.retryAfterSeconds;
      if (seconds === null) return 'Demasiados intentos. Espera un poco.';
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      return `Demasiados intentos. Espera ${minutes} minuto${minutes === 1 ? '' : 's'}.`;
    }
    return 'No se pudo completar el registro. Revisa los datos.';
  }

  const topError = topErrorMessage();

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Crea tu cuenta</h2>
        <p className="mt-1 text-sm text-slate-600">
          Sólo el email, contraseña y nombre son obligatorios.
        </p>
      </header>

      <Field id="email" label="Email *" error={fieldErrors.email}>
        <input
          id="email"
          type="email"
          required
          autoComplete="email"
          value={state.email}
          onChange={(e) => update('email', e.target.value)}
          aria-invalid={fieldErrors.email !== undefined}
          aria-describedby={fieldErrors.email ? 'email-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <Field id="password" label="Contraseña *" error={fieldErrors.password} hint="Mínimo 8 caracteres">
        <input
          id="password"
          type="password"
          required
          autoComplete="new-password"
          minLength={8}
          value={state.password}
          onChange={(e) => update('password', e.target.value)}
          aria-invalid={fieldErrors.password !== undefined}
          aria-describedby={fieldErrors.password ? 'password-error password-strength' : 'password-hint password-strength'}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <div id="password-strength" aria-live="polite" className="text-xs text-slate-600">
        <div className="flex h-1.5 w-full overflow-hidden rounded bg-slate-200" aria-hidden="true">
          <div
            className={`h-full transition-all ${strength.color}`}
            style={{ width: `${(strength.score / 4) * 100}%` }}
          />
        </div>
        <p className="mt-1">Fuerza: {strength.label}</p>
      </div>

      <Field id="passwordConfirm" label="Confirmar contraseña *" error={fieldErrors.passwordConfirm}>
        <input
          id="passwordConfirm"
          type="password"
          required
          autoComplete="new-password"
          value={state.passwordConfirm}
          onChange={(e) => update('passwordConfirm', e.target.value)}
          aria-invalid={fieldErrors.passwordConfirm !== undefined}
          aria-describedby={fieldErrors.passwordConfirm ? 'passwordConfirm-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <Field id="name" label="Nombre *" error={fieldErrors.name}>
        <input
          id="name"
          type="text"
          required
          autoComplete="name"
          value={state.name}
          onChange={(e) => update('name', e.target.value)}
          aria-invalid={fieldErrors.name !== undefined}
          aria-describedby={fieldErrors.name ? 'name-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field id="age" label="Edad" error={fieldErrors.age}>
          <input
            id="age"
            type="number"
            inputMode="numeric"
            min={10}
            max={120}
            value={state.age}
            onChange={(e) => update('age', e.target.value)}
            aria-invalid={fieldErrors.age !== undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>

        <Field id="sex" label="Sexo">
          <select
            id="sex"
            value={state.sex}
            onChange={(e) => update('sex', e.target.value as FormState['sex'])}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          >
            <option value="">— sin especificar —</option>
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
            <option value="X">Otro / prefiero no decir</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field id="weightKg" label="Peso (kg)" error={fieldErrors.weightKg}>
          <input
            id="weightKg"
            type="number"
            inputMode="decimal"
            step="0.1"
            min={1}
            max={400}
            value={state.weightKg}
            onChange={(e) => update('weightKg', e.target.value)}
            aria-invalid={fieldErrors.weightKg !== undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>

        <Field id="heightCm" label="Talla (cm)" error={fieldErrors.heightCm}>
          <input
            id="heightCm"
            type="number"
            inputMode="numeric"
            step="1"
            min={1}
            max={280}
            value={state.heightCm}
            onChange={(e) => update('heightCm', e.target.value)}
            aria-invalid={fieldErrors.heightCm !== undefined}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
          />
        </Field>
      </div>

      <Field id="timezone" label="Timezone *" error={fieldErrors.timezone}>
        <input
          id="timezone"
          type="text"
          value={state.timezone}
          onChange={(e) => update('timezone', e.target.value)}
          aria-invalid={fieldErrors.timezone !== undefined}
          aria-describedby={fieldErrors.timezone ? 'timezone-error' : undefined}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
      </Field>

      <fieldset>
        <legend className="block text-sm font-medium text-slate-700">Unidades</legend>
        <div className="mt-1 flex gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="units"
              value="metric"
              checked={state.units === 'metric'}
              onChange={() => update('units', 'metric')}
            />
            Métrico (km, kg)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              name="units"
              value="imperial"
              checked={state.units === 'imperial'}
              onChange={() => update('units', 'imperial')}
            />
            Imperial (mi, lb)
          </label>
        </div>
      </fieldset>

      {topError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {topError}
        </p>
      )}

      {signupMutation.isSuccess && (
        <p role="status" className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Cuenta creada. Redirigiendo…
        </p>
      )}

      <button
        type="submit"
        disabled={signupMutation.isPending}
        aria-busy={signupMutation.isPending}
        className="w-full rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {signupMutation.isPending ? 'Creando…' : 'Crear cuenta'}
      </button>

      <p className="text-sm text-slate-600">
        ¿Ya tienes cuenta?{' '}
        <Link
          to="/login"
          className="text-teal-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Inicia sesión
        </Link>
      </p>
    </form>
  );
}

type FieldProps = {
  id: string;
  label: string;
  // `string | undefined` (no opcional) en lugar de `error?: string` para que
  // sea compatible con `exactOptionalPropertyTypes: true` cuando el caller
  // pasa `error={errors.x}` (que ya es `string | undefined`).
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactElement;
};

function Field({ id, label, error, children, hint }: FieldProps): ReactElement {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children}
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-rose-700">
          {error}
        </p>
      )}
    </div>
  );
}
